import { ChangeEvent, CSSProperties, FC, ReactElement } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { FileUploadOutlined } from "@mui/icons-material";
import { tokens } from "@/tokens";

interface IPictureUploader {
  title: string;
  description: string;
  customPreviewType?: "circle" | "rounded" | "default";
  imgUrl?: string;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

const imageStyles = {
  circle: {
    borderRadius: "50%",
    imgSize: {
      width: "100%",
      height: "100%",
      objectFit: "cover" as CSSProperties["objectFit"],
    },
  },
  rounded: {
    borderRadius: "12px",
    imgSize: {
      width: "100%",
      height: "100%",
      objectFit: "cover" as CSSProperties["objectFit"],
    },
  },
  default: {
    borderRadius: "0",
    imgSize: {},
  },
};

const PictureUploader: FC<IPictureUploader> = ({
  title,
  description,
  customPreviewType = "default",
  imgUrl,
  onChange,
  disabled,
  isLoading,
}): ReactElement => {
  const styles = imageStyles[customPreviewType];
  const { borderRadius, imgSize } = styles;

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    onChange(file);
  };
  return (
    <Stack mt={3.75} direction="row" gap={3.75}>
      <Box
        sx={{
          width: 104,
          height: 104,
          borderRadius: borderRadius,
          display: "grid",
          placeItems: "center",
          overflow: "hidden",
          background: tokens.border.default,
        }}
      >
        {isLoading ? <CircularProgress size='22px' /> : null}
        {!isLoading && imgUrl && <img src={imgUrl} style={imgSize} />}
      </Box>
      <Box flex={1} mb={5}>
        <Typography
          fontSize={18}
          color={tokens.text.primary}
          fontWeight={tokens.fontWeight.semibold}
          lineHeight="23px"
        >
          {title}
        </Typography>
        <Typography variant="caption" color="#777E90" sx={{ display: "block" }}>
          {description}
        </Typography>
        <Button
          startIcon={<FileUploadOutlined />}
          variant="contained"
          role={undefined}
          disabled={disabled}
          tabIndex={-1}
          component="label"
          sx={{
            px: 2,
            py: 1.25,
            lineHeight: "20px",
            fontWeight: tokens.fontWeight.semibold,
            mt: 2,
          }}
        >
          Upload Image
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={handleFileChange}
            multiple={false}
          />
        </Button>
      </Box>
    </Stack>
  );
};

export default PictureUploader;
